import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 硬核科普与通识教育 - 横屏「信息图表」风格
 *
 * 视觉特点：杂志级信息图表、横向数据流、柱状图动画、多区域网格布局
 * 适合：数据密集型科普、对比分析、趋势解读等需要多维度呈现的内容
 * 尺寸：1920x1080 横屏
 */

interface BarData {
  label: string;
  value: number;
  color: string;
}

interface TimelineEvent {
  year: string;
  event: string;
}

interface ScienceInfographicProps {
  topic: string;
  hookStat: { number: string; label: string };
  description: string;
  barChart: { title: string; data: BarData[]; unit: string };
  timeline: { title: string; events: TimelineEvent[] };
  factCards: { icon: string; title: string; detail: string }[];
  source: string;
}

const DEFAULT_PROPS: ScienceInfographicProps = {
  topic: '全球变暖：数据背后的真相',
  hookStat: { number: '1.1°C', label: '全球平均温度已比工业化前上升' },
  description: '过去 170 年的数据揭示了一个加速变暖的趋势，而最近 50 年的变化尤为剧烈',
  barChart: {
    title: '近五十年全球温度变化（相对于 1951-1980 基准）',
    data: [
      { label: '1980', value: 26, color: '#fbbf24' },
      { label: '1990', value: 43, color: '#f97316' },
      { label: '2000', value: 58, color: '#ef4444' },
      { label: '2010', value: 72, color: '#dc2626' },
      { label: '2020', value: 98, color: '#b91c1c' },
    ],
    unit: '温度异常 (×0.01°C)',
  },
  timeline: {
    title: '关键里程碑',
    events: [
      { year: '1988', event: 'IPCC 成立' },
      { year: '1997', event: '京都议定书' },
      { year: '2015', event: '巴黎协定' },
      { year: '2023', event: '史上最热年' },
    ],
  },
  factCards: [
    { icon: '🌊', title: '海平面上升', detail: '过去 100 年上升约 20cm' },
    { icon: '🧊', title: '冰川消融', detail: '北极冰量每十年减少 13%' },
    { icon: '🌪️', title: '极端天气', detail: '强台风频率增加 25%' },
  ],
  source: 'IPCC AR6 报告 | NASA GISS 数据集',
};

export const ScienceInfographicTemplate: React.FC<Partial<ScienceInfographicProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Header (0-40)
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 100 } });

  // Hook stat (20-80)
  const statScale = spring({ frame: frame - 20, fps, from: 0.5, to: 1, config: { damping: 12, stiffness: 100 } });
  const statOpacity = interpolate(frame, [20, 40], [0, 1], { extrapolateRight: 'clamp' });

  // Bar chart (100-250)
  const barStart = 100;

  // Timeline (260-380)
  const timelineStart = 260;

  // Fact cards (300-400)
  const factStart = 300;

  // Source
  const sourceOpacity = interpolate(frame, [420, 450], [0, 1], { extrapolateRight: 'clamp' });

  const maxBarValue = Math.max(...p.barChart.data.map((d) => d.value));

  return (
    <AbsoluteFill
      style={{
        background: '#fafaf9',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: '#1c1917',
        overflow: 'hidden',
      }}
    >
      {/* Subtle texture */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.02) 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* Accent top bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 5,
          background: 'linear-gradient(90deg, #ef4444, #f97316, #fbbf24)',
        }}
      />

      {/* LEFT COLUMN — Topic + Hook stat + Description */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 50,
          width: '38%',
          bottom: 50,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Topic */}
        <h1
          style={{
            fontSize: 44,
            fontWeight: 800,
            margin: 0,
            lineHeight: 1.3,
            opacity: headerProgress,
            transform: `translateY(${(1 - headerProgress) * -20}px)`,
          }}
        >
          {p.topic}
        </h1>

        {/* Hook stat */}
        <div
          style={{
            marginTop: 32,
            padding: '28px 32px',
            background: 'linear-gradient(135deg, #fef2f2, #fff7ed)',
            borderRadius: 20,
            borderLeft: '5px solid #ef4444',
            opacity: statOpacity,
            transform: `scale(${statScale})`,
          }}
        >
          <div style={{ fontSize: 56, fontWeight: 900, color: '#dc2626', letterSpacing: '-0.03em' }}>
            {p.hookStat.number}
          </div>
          <div style={{ fontSize: 28, color: '#78716c', marginTop: 8, lineHeight: 1.4 }}>{p.hookStat.label}</div>
        </div>

        {/* Description */}
        <div
          style={{
            marginTop: 28,
            fontSize: 26,
            lineHeight: 1.7,
            color: '#57534e',
            opacity: interpolate(frame, [60, 85], [0, 1], { extrapolateRight: 'clamp' }),
          }}
        >
          {p.description}
        </div>

        {/* Fact cards */}
        <div style={{ marginTop: 'auto', display: 'flex', gap: 12 }}>
          {p.factCards.map((card, i) => {
            const start = factStart + i * 25;
            const cardProgress = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 14, stiffness: 140, mass: 0.5 },
            });

            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  padding: '24px 14px',
                  background: 'white',
                  borderRadius: 20,
                  border: '1px solid #e7e5e4',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  opacity: cardProgress,
                  transform: `translateY(${(1 - cardProgress) * 20}px)`,
                }}
              >
                <div style={{ fontSize: 36, marginBottom: 6 }}>{card.icon}</div>
                <div style={{ fontSize: 26, fontWeight: 700 }}>{card.title}</div>
                <div style={{ fontSize: 22, color: '#78716c', marginTop: 4, lineHeight: 1.4 }}>{card.detail}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT COLUMN — Bar chart + Timeline */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          right: 50,
          width: '55%',
          bottom: 50,
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
        }}
      >
        {/* Bar chart */}
        <div
          style={{
            background: 'white',
            borderRadius: 20,
            padding: '28px 32px',
            border: '1px solid #e7e5e4',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            flex: 1,
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 4 }}>{p.barChart.title}</div>
          <div style={{ fontSize: 26, color: '#a8a29e', marginBottom: 24 }}>{p.barChart.unit}</div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, height: 280, paddingBottom: 40, position: 'relative', marginTop: 100 }}>
            {/* Y-axis grid lines */}
            {[0, 25, 50, 75, 100].map((tick) => (
              <div
                key={tick}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 40 + (tick / 100) * 240,
                  height: 1,
                  background: tick === 0 ? '#d6d3d1' : '#f5f5f4',
                }}
              />
            ))}

            {p.barChart.data.map((bar, i) => {
              const start = barStart + i * 25;
              const barHeight = interpolate(frame, [start, start + 35], [0, (bar.value / maxBarValue) * 240], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              });

              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                  {/* Value label */}
                  <div
                    style={{
                      fontSize: 24,
                      fontWeight: 700,
                      color: bar.color,
                      marginBottom: 8,
                      opacity: interpolate(frame, [start + 20, start + 35], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }),
                    }}
                  >
                    +{bar.value}
                  </div>
                  {/* Bar */}
                  <div
                    style={{
                      width: '70%',
                      height: barHeight,
                      background: `linear-gradient(180deg, ${bar.color}, ${bar.color}cc)`,
                      borderRadius: '8px 8px 4px 4px',
                      marginTop: 'auto',
                    }}
                  />
                  {/* Label */}
                  <div style={{ fontSize: 24, color: '#78716c', marginTop: 12 }}>{bar.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline */}
        <div
          style={{
            background: 'white',
            borderRadius: 20,
            padding: '24px 32px',
            border: '1px solid #e7e5e4',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 20 }}>{p.timeline.title}</div>
          <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            {/* Timeline line */}
            <div
              style={{
                position: 'absolute',
                top: 14,
                left: 0,
                right: 0,
                height: 3,
                background: '#e7e5e4',
                borderRadius: 2,
              }}
            />

            {p.timeline.events.map((evt, i) => {
              const start = timelineStart + i * 30;
              const dotScale = spring({
                frame: frame - start,
                fps,
                from: 0,
                to: 1,
                config: { damping: 12, stiffness: 150, mass: 0.5 },
              });

              return (
                <div key={i} style={{ flex: 1, textAlign: 'center', position: 'relative', zIndex: 1 }}>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: i === p.timeline.events.length - 1 ? '#ef4444' : '#6366f1',
                      margin: '0 auto',
                      transform: `scale(${dotScale})`,
                      border: '4px solid white',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                    }}
                  />
                  <div style={{ fontSize: 22, fontWeight: 700, marginTop: 12, opacity: dotScale }}>{evt.year}</div>
                  <div style={{ fontSize: 22, color: '#78716c', marginTop: 4, opacity: dotScale }}>{evt.event}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Source bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          right: 50,
          opacity: sourceOpacity,
          fontSize: 14,
          color: '#a8a29e',
        }}
      >
        📎 {p.source}
      </div>
    </AbsoluteFill>
  );
};
